import useSWR from 'swr'
import { useParams } from 'react-router-dom'

import { getMultivertrekken } from '../data/travelinfo-trip.api'
import { IRTIS3_REFRESH_INTERVAL } from '../constants'
import { useContext } from 'react'
import { ServerTimeContext } from '../contexts/serverTime.context'
import { filterPassagesAfterDate } from '../utils/date.utils'
import { DisplayPassage } from '../data/mappers'

export function useMultivertrekken({
  includeEindHaltes,
  stopIds,
}: {
  stopIds?: string
  includeEindHaltes: boolean
}) {
  const { updateServerTime, getCalibratedDateTime } =
    useContext(ServerTimeContext)
  const { haltenummers } = useParams()
  const { data, error, isLoading } = useSWR(
    `multimaxi-${stopIds ?? haltenummers}`,
    () =>
      getMultivertrekken({
        haltenummers: stopIds ?? (haltenummers as string),
        includeEindHaltes,
      }),
    {
      refreshInterval: IRTIS3_REFRESH_INTERVAL,
      fallbackData: {
        passages: [],
        serverTime: getCalibratedDateTime().getTime(),
      },
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateIfStale: true,
      revalidateOnReconnect: true,
      onErrorRetry: (_error, _key, config, revalidate, { retryCount }) => {
        // Reset realtime data after 5 minutes (20 retries * 15 seconds) to fallback on theoretical data
        const errorTimeoutId = setTimeout(() => {
          //  4 * 15 =  60; 1 minute
          // 20 * 15 = 300; 5 minutes
          if (retryCount === 20) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            config.cache.delete(`multimaxi-${haltenummers}`)
            console.debug('Removed realtime data after 5 minutes')
          }
          revalidate({ retryCount })
          clearTimeout(errorTimeoutId)
        }, IRTIS3_REFRESH_INTERVAL)
      },
      onSuccess: ({
        serverTime = getCalibratedDateTime().getTime(),
      }: {
        passages: DisplayPassage[]
        serverTime: number
      }) => {
        updateServerTime(serverTime)
      },
    },
  )

  if (error) {
    return {
      data: {
        passages: filterPassagesAfterDate(
          data.passages,
          getCalibratedDateTime(),
        ),
        serverTime: data.serverTime,
      },
      error,
      isLoading,
    }
  }

  return {
    data,
    error,
    isLoading,
  }
}
